// Dots and Boxes engine (pure). A grid of dots; players take turns drawing one line between two neighboring dots.
// Drawing the fourth side of a box claims it, and whoever claims a box draws again. When every line is drawn, the
// most boxes wins. Sizes are counted in boxes: 3 by 3, 4 by 4 or 5 by 5.
//
// Lines are numbered: first the horizontal ones, row by row ((rows + 1) × cols of them), then the vertical ones
// (rows × (cols + 1)). Boxes are numbered row by row.

export type Side = "you" | "cpu"
export type Level = "easy" | "medium" | "hard"
export const LEVELS: Level[] = ["easy", "medium", "hard"]
export const SIZES = [3, 4, 5] as const

export interface DotsState {
  rows: number
  cols: number
  lines: (Side | null)[]
  boxes: (Side | null)[]
  turn: Side
  over: null | { winner: Side | "draw"; you: number; cpu: number }
  moves: number // lines you've drawn
}

export const lineCount = (rows: number, cols: number): number => (rows + 1) * cols + rows * (cols + 1)

export function newGame(size = 4): DotsState {
  return { rows: size, cols: size, lines: new Array(lineCount(size, size)).fill(null), boxes: new Array(size * size).fill(null), turn: "you", over: null, moves: 0 }
}

/** The line's place: horizontal or vertical, and its row and column among the dots. */
export function lineAt(s: { rows: number; cols: number }, l: number): { horizontal: boolean; r: number; c: number } {
  const h = (s.rows + 1) * s.cols
  return l < h ? { horizontal: true, r: Math.floor(l / s.cols), c: l % s.cols } : { horizontal: false, r: Math.floor((l - h) / (s.cols + 1)), c: (l - h) % (s.cols + 1) }
}

/** The four sides of box b: top, bottom, left, right. */
export function sides(s: { rows: number; cols: number }, b: number): [number, number, number, number] {
  const r = Math.floor(b / s.cols), c = b % s.cols, h = (s.rows + 1) * s.cols
  return [r * s.cols + c, (r + 1) * s.cols + c, h + r * (s.cols + 1) + c, h + r * (s.cols + 1) + c + 1]
}

/** The one or two boxes a line borders. */
export function boxesOf(s: { rows: number; cols: number }, l: number): number[] {
  const { horizontal, r, c } = lineAt(s, l)
  const out: number[] = []
  if (horizontal) { if (r > 0) out.push((r - 1) * s.cols + c); if (r < s.rows) out.push(r * s.cols + c) }
  else { if (c > 0) out.push(r * s.cols + c - 1); if (c < s.cols) out.push(r * s.cols + c) }
  return out
}

const open = (s: DotsState, b: number): number => sides(s, b).filter((l) => !s.lines[l]).length

export const legalMoves = (s: DotsState): number[] => (s.over ? [] : s.lines.flatMap((x, l) => (x ? [] : [l])))
export const score = (s: DotsState, side: Side): number => s.boxes.filter((x) => x === side).length

/** Draws line `l` for the side to move. Returns the new state and how many boxes it completed. */
export function draw(s: DotsState, l: number): [DotsState, number] {
  const lines = s.lines.slice(), boxes = s.boxes.slice()
  lines[l] = s.turn
  let made = 0
  for (const b of boxesOf(s, l)) if (!boxes[b] && sides(s, b).every((x) => lines[x])) { boxes[b] = s.turn; made++ }
  const next: DotsState = { ...s, lines, boxes, turn: made ? s.turn : s.turn === "you" ? "cpu" : "you", moves: s.moves + (s.turn === "you" ? 1 : 0) }
  if (lines.every((x) => x)) {
    const you = score(next, "you"), cpu = score(next, "cpu")
    next.over = { winner: you > cpu ? "you" : cpu > you ? "cpu" : "draw", you, cpu }
  }
  return [next, made]
}
export const play = (s: DotsState, l: number): DotsState => draw(s, l)[0]

// ---- the computer ------------------------------------------------------------------------------------------------

/** Lines that complete a box now. */
export const captures = (s: DotsState): number[] => legalMoves(s).filter((l) => boxesOf(s, l).some((b) => open(s, b) === 1))
/** Lines that don't give the other side a box: none of their boxes is left with one side open. */
export const safeLines = (s: DotsState): number[] => legalMoves(s).filter((l) => boxesOf(s, l).every((b) => open(s, b) >= 3))

/** How many boxes the side to move can take in a row from here, taking every box it can. */
function greedy(s: DotsState): [number, DotsState] {
  let n = 0, t = s
  for (;;) {
    const c = captures(t)
    if (!c.length || t.over) return [n, t]
    const [u, made] = draw(t, c[0]!)
    n += made; t = u
  }
}

/** The best net score (own boxes minus the other side's) for the player to move in an endgame of separate chains and
 *  loops, where every move opens one of them (Berlekamp's simple loony endgame). The answer to an opened chain of 3 or
 *  more, or a loop, is either to take it all and move next, or to take all but 2 (4 of a loop) and keep control. */
const memo = new Map<string, number>()
export function endgameValue(chains: number[], loops: number[]): number {
  if (!chains.length && !loops.length) return 0
  const key = `${chains.slice().sort((a, b) => a - b).join(",")}|${loops.slice().sort((a, b) => a - b).join(",")}`
  const hit = memo.get(key)
  if (hit !== undefined) return hit
  let best = -Infinity
  chains.forEach((n, i) => {
    const rest = endgameValue(chains.filter((_, j) => j !== i), loops)
    const theirs = n >= 3 ? Math.max(n + rest, n - 4 - rest) : n + rest
    best = Math.max(best, -theirs)
  })
  loops.forEach((n, i) => {
    const rest = endgameValue(chains, loops.filter((_, j) => j !== i))
    best = Math.max(best, -Math.max(n + rest, n - 8 - rest))
  })
  best ||= 0 // not -0
  memo.set(key, best)
  return best
}

/** The chains and loops left when every unclaimed box has exactly two open sides, or null when it doesn't. */
export function components(s: DotsState): { chains: number[]; loops: number[] } | null {
  const free = s.boxes.flatMap((x, b) => (x ? [] : [b]))
  if (free.some((b) => open(s, b) !== 2)) return null
  const seen = new Set<number>()
  const chains: number[] = [], loops: number[] = []
  const neighbours = (b: number): (number | null)[] => sides(s, b).filter((l) => !s.lines[l]).map((l) => boxesOf(s, l).find((x) => x !== b) ?? null)
  for (const start of free) {
    if (seen.has(start)) continue
    // Walk both ways from `start`; null is the edge of the board.
    seen.add(start)
    let n = 1, ends = 0
    for (const first of neighbours(start)) {
      let prev = start, at = first
      while (at !== null && !seen.has(at)) {
        seen.add(at); n++
        const next: number | null = neighbours(at).find((x) => x !== prev) ?? null
        prev = at; at = next
      }
      if (at === null) ends++
    }
    if (ends === 0) loops.push(n)
    else chains.push(n)
  }
  return { chains, loops }
}

/** The line the side to move should draw. */
export function chooseLine(s: DotsState, level: Level, rand: () => number = Math.random): number {
  const pick = <T>(xs: T[]): T => xs[Math.floor(rand() * xs.length)]!
  const caps = captures(s), safe = safeLines(s), all = legalMoves(s)
  if (level === "easy") {
    if (caps.length && rand() < 0.6) return pick(caps)
    if (safe.length && rand() < 0.5) return pick(safe)
    return pick(all)
  }
  if (caps.length) {
    if (level === "hard") { const dd = doubleDeal(s); if (dd !== null) return dd }
    // Prefer a line that completes two boxes at once.
    const two = caps.filter((l) => boxesOf(s, l).filter((b) => open(s, b) === 1).length === 2)
    return pick(two.length ? two : caps)
  }
  if (safe.length) return pick(safe)
  return fewestGiven(s, all, rand)
}

/** Opening move when every line gives something away: the one that hands over the fewest boxes; for a chain of two,
 *  the line between its boxes, so the other side can't keep control by declining them. */
function fewestGiven(s: DotsState, all: number[], rand: () => number): number {
  let best: number[] = [], bestN = Infinity
  for (const l of all) {
    const after = play(s, l)
    const [n] = greedy(after)
    const middle = boxesOf(s, l).length === 2 && boxesOf(s, l).every((b) => open(after, b) === 1) ? -0.5 : 0
    const v = n + middle
    if (v < bestN) { bestN = v; best = [l] } else if (v === bestN) best.push(l)
  }
  return best[Math.floor(rand() * best.length)]!
}

/** When exactly two boxes of a chain are left to take and taking them would leave us to open the next chain at a
 *  loss, decline them: draw the far side of the second box, handing both over, so the other side must open next. */
function doubleDeal(s: DotsState): number | null {
  const caps = captures(s)
  const [k, after] = greedy(s)
  if (k !== 2 || after.over || safeLines(after).length) return null
  // The two boxes must be one capturable box and its neighbour along the chain.
  const ready = s.boxes.flatMap((x, b) => (!x && open(s, b) === 1 ? [b] : []))
  if (ready.length !== 1 || caps.length !== 1) return null
  const b1 = ready[0]!, l12 = caps[0]!
  const b2 = boxesOf(s, l12).find((b) => b !== b1)
  if (b2 === undefined || open(s, b2) !== 2) return null
  const far = sides(s, b2).find((l) => !s.lines[l] && l !== l12)!
  if (boxesOf(s, far).some((b) => b !== b2 && open(s, b) <= 2)) return null // the chain goes on past b2
  const comp = components(after)
  if (!comp) return null
  return endgameValue(comp.chains, comp.loops) < -2 ? far : null
}

/** Hint: what Medium would play for you. */
export const hintLine = (s: DotsState): number => chooseLine(s, "medium", () => 0)

/** Names a line for people, counting dots from the top left: "across, row 2, dots 3 to 4" or "down, column 1, dots
 *  2 to 3". */
export function lineName(s: { rows: number; cols: number }, l: number): string {
  const { horizontal, r, c } = lineAt(s, l)
  return horizontal ? `across, row ${r + 1}, dots ${c + 1} to ${c + 2}` : `down, column ${c + 1}, dots ${r + 1} to ${r + 2}`
}
